import { Card } from 'components/Card';
import { CardRow } from 'components/CardRow';
import { Screens } from 'components/Navigation/types';
import { useSelector } from 'hooks/useSelector';
import { theme } from 'lib/theme';
import { isEqual } from 'lodash';
import React from 'react';
import { useDispatch } from 'react-redux';
import {
  doAseOpenSection,
  doAseSetSectionIsCollapsed,
} from 'store/assessments/actions';
import {
  selectAseTplSectionQuestionIds,
  selectAseSectionActionIds,
  selectAseSectionControlIds,
  selectAseTplSectionIsVisible,
  selectAseSectionUnreviewedActionIds,
  selectAseSectionUnreviewedControlIds,
  selectAseSection,
  selectAseExplorerOptionCollapseFinding,
  selectAseIsSectionCollapsed,
} from 'store/assessments/selectors';

import { useNavigationIsSelectedMulti } from 'hooks/useNavigationIsSelected';
import { ExplorerActionRow } from './ExplorerActionRow';
import { ExplorerControlRow } from './ExplorerControlRow';
import { ExplorerQuestion } from './ExplorerQuestion';
import { ExplorerSectionRow } from './ExplorerSectionRow';

interface Props {
  id: string;
}

export const ExplorerSection: React.FC<Props> = ({ id }) => {
  const dispatch = useDispatch();

  const isSectionCollapsed = useSelector((state) =>
    selectAseIsSectionCollapsed(state, id),
  );
  const collapseFindings = useSelector(selectAseExplorerOptionCollapseFinding);
  const templateSectionIsVisible = useSelector((state) =>
    selectAseTplSectionIsVisible(state, id),
  );

  const result = useSelector((state) => selectAseSection(state, id));

  const actionIds = useSelector(
    (state) => selectAseSectionActionIds(state, id),
    isEqual,
  );
  const controlIds = useSelector(
    (state) => selectAseSectionControlIds(state, id),
    isEqual,
  );
  const unreviewedActionIds = useSelector(
    (state) => selectAseSectionUnreviewedActionIds(state, id),
    isEqual,
  );
  const unreviewedControlIds = useSelector(
    (state) => selectAseSectionUnreviewedControlIds(state, id),
    isEqual,
  );
  const questionIds = useSelector((state) =>
    selectAseTplSectionQuestionIds(state, id),
  );

  const handleToggleCollapse = React.useCallback(() => {
    dispatch(
      doAseSetSectionIsCollapsed({
        sectionId: id,
        isCollapsed: !isSectionCollapsed,
      }),
    );
  }, [dispatch, id, isSectionCollapsed]);

  const selected = useNavigationIsSelectedMulti(
    collapseFindings
      ? [
          {
            name: Screens.ASE_SECTION,
            params: { id },
          },
          ...[...unreviewedActionIds, ...actionIds].map((actionId) => ({
            name: Screens.ASE_ACTION,
            params: { id: actionId },
          })),
          ...[...unreviewedControlIds, ...controlIds].map((controlId) => ({
            name: Screens.ASE_CONTROL,
            params: { id: controlId },
          })),
        ]
      : [],
  );

  if (!templateSectionIsVisible) {
    return null;
  }

  const isOther = id === '1';
  const isOtherWithFindings =
    isOther &&
    (!!actionIds.length ||
      !!controlIds.length ||
      !!unreviewedActionIds.length ||
      !!unreviewedControlIds.length);

  if (isOther && !result?.comment && !isOtherWithFindings) {
    return null;
  }

  return (
    <Card
      separator={null}
      style={{ marginTop: theme.paddingVertical / 2 }}
      margin={false}
    >
      <CardRow
        navigationIsSelected={{ name: Screens.ASE_SECTION, params: { id } }}
        onPress={() => dispatch(doAseOpenSection({ id }))}
        style={{ paddingLeft: 0, paddingRight: 0, minHeight: 0 }}
        contentStyle={{ paddingTop: 0, paddingBottom: 0 }}
      >
        <ExplorerSectionRow
          id={id}
          collapsed={isSectionCollapsed}
          collapsible={isOther ? isOtherWithFindings : true}
          onToggleCollapsedPress={handleToggleCollapse}
          actionsCount={!selected && collapseFindings ? actionIds.length : 0}
          controlsCount={!selected && collapseFindings ? controlIds.length : 0}
          unreviewedActionsCount={
            !selected && collapseFindings ? unreviewedActionIds.length : 0
          }
          unreviewedControlsCount={
            !selected && collapseFindings ? unreviewedControlIds.length : 0
          }
        />
        {!isSectionCollapsed &&
          (selected || !collapseFindings) &&
          (!!actionIds.length ||
            !!controlIds.length ||
            !!unreviewedActionIds.length ||
            !!unreviewedControlIds.length) && (
            <Card
              expanded
              margin={false}
              style={{
                marginBottom: theme.paddingHorizontal,
              }}
              bodyStyle={{
                borderColor: '#f1f1f1',
              }}
            >
              {actionIds.map((actionId) => (
                <ExplorerActionRow
                  key={`action-${actionId}`}
                  id={actionId}
                  parent={null}
                />
              ))}
              {controlIds.map((controlId) => (
                <ExplorerControlRow
                  key={`control-${controlId}`}
                  id={controlId}
                  parent={null}
                />
              ))}
              {unreviewedActionIds.map((actionId) => (
                <ExplorerActionRow
                  key={`reviewaction-${actionId}`}
                  id={actionId}
                  parent={null}
                />
              ))}
              {unreviewedControlIds.map((controlId) => (
                <ExplorerControlRow
                  key={`reviewcontrol-${controlId}`}
                  id={controlId}
                  parent={null}
                />
              ))}
            </Card>
          )}
      </CardRow>
      {!isSectionCollapsed &&
        questionIds.map((questionId) => (
          <ExplorerQuestion
            key={`q-${questionId}`}
            id={questionId}
            fieldInstanceId={null}
            parent={null}
          />
        ))}
    </Card>
  );
};
